// ModelViewer — single entry point that routes a model to the right viewer:
//   'single'      .splatv / .splat            → SplatViewer (WebGL2 splaTV)
//   'ply'         single .ply                 → DualgsViewer (three.js + Spark)
//   'sequence'    .4dms / .plyseq per-frame   → DualgsViewer (frameUrls playback)
//   'clipgstream' ClipGStream dataset (zip)   → ClipGStreamViewer (WebGPU)
//   'fourdv'      single .4dv (4D Gaussians)  → FourDViewer (PlayCanvas, lazy-loaded)
//
// Common props (className / camera sync / onCameraChange / onCanvasReady) are
// unified here; anything viewer-specific goes through the `splat` / `dualgs` /
// `clipgs` / `fourdv` pass-through objects, which are spread last so they can
// also override the common props for that viewer.
import { forwardRef, lazy, Suspense, useEffect, useState, type ComponentProps } from 'react'
import { SplatViewer } from '../SplatViewer'
// @ts-expect-error - JSX module without type declarations
import DualgsViewer from '../DualgsViewer/DualgsViewer'
// @ts-expect-error - JSX module without type declarations
import ClipGStreamViewer from '../ClipGStreamViewer/ClipGStreamViewer'
// @ts-expect-error - JSX module without type declarations
import ClipGStreamViewerXR, { isLimitedDevice } from '../ClipGStreamViewer/ClipGStreamViewerXR'
import type { Quaternion } from '@/utils/quaternion'
import type { ModelSource } from './resolveModelSource'
// type-only: erased at build time, so it does not pull the lazy chunk into this one
import type { FourDViewerHandle, FourDViewerProps } from '../FourDViewer/FourDViewer'

// PlayCanvas (~1 MB minified) must stay out of the main bundle: the .4dv viewer is
// fetched only when a 'fourdv' model is actually shown.
// A failed chunk download (network, or a tab older than the current deploy) must not reach
// React as a thrown error: nothing above here has an error boundary, so the whole app would go
// blank. Render the error inside the viewer instead.
const FourDViewer = lazy(() => import('../FourDViewer/FourDViewer').catch((err: unknown) => ({
  // forwardRef only to match the real component's type; there is no handle to expose (React
  // warns about a render function that does not take both props and ref)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  default: forwardRef<FourDViewerHandle, FourDViewerProps>(function FourDViewerLoadError({ className }, _ref) {
    return (
      <div
        className={className}
        style={{ position: 'relative', background: '#000', color: '#f44', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}
      >
        Error: the 4D viewer failed to load ({err instanceof Error ? err.message : String(err)}). Reload the page to retry.
      </div>
    )
  }),
})))

/**
 * Robustly decide whether this is a "limited" (non-PC) device — mobile phone or
 * XR headset — so clipgstream routes to the streaming viewer instead of the
 * memory-heavy WebGPU full-preload one, and a large .4dv streams instead of
 * preloading (and offers no XR).
 *
 * UA alone is unreliable: Meta Quest Browser freezes/trims its UA and may not
 * contain "OculusBrowser"/"Quest", so it slips through as a desktop Linux Chrome.
 * We therefore combine a synchronous UA fast-path with an async WebXR probe:
 * a real headset reports immersive-vr/ar support; a desktop PC (no headset)
 * does not. Returns `null` while probing so the caller can hold off mounting
 * the wrong viewer.
 */
function useIsLimitedDevice(): boolean | null {
  const [limited, setLimited] = useState<boolean | null>(() => {
    if (isLimitedDevice()) return true                                   // UA: phone / tablet / known headset
    if (typeof navigator === 'undefined' || !('xr' in navigator)) return false // no WebXR → desktop
    return null                                                          // has navigator.xr → probe below
  })
  useEffect(() => {
    if (limited !== null) return
    let cancelled = false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const xr = (navigator as any).xr
    if (!xr?.isSessionSupported) { setLimited(false); return }
    Promise.all([
      xr.isSessionSupported('immersive-vr').catch(() => false),
      xr.isSessionSupported('immersive-ar').catch(() => false),
    ]).then(([vr, ar]: [boolean, boolean]) => { if (!cancelled) setLimited(!!(vr || ar)) })
    return () => { cancelled = true }
  }, [limited])
  return limited
}

export interface ModelViewerProps {
  source: ModelSource
  className?: string
  cameraPosition?: [number, number, number] | null
  cameraQuaternion?: Quaternion | null
  onCameraChange?: (position: [number, number, number], quaternion: Quaternion) => void
  onCanvasReady?: (canvas: HTMLCanvasElement) => void
  /** Enable WebXR (AR/VR). Mirrors DualgsViewer's `enableXR`: shows the XR enter
   *  button when the device supports it. Routing never depends on it: 'clipgstream'
   *  picks its viewer by device (the streaming ClipGStreamViewerXR on phones and
   *  headsets, which alone gets this flag; the desktop WebGPU viewer has no XR). */
  enableXR?: boolean
  /** Extra props forwarded verbatim to SplatViewer (kind 'single'). */
  splat?: Partial<ComponentProps<typeof SplatViewer>>
  /** Extra props forwarded verbatim to DualgsViewer (kind 'ply' | 'sequence'). */
  dualgs?: Record<string, unknown>
  /** Extra props forwarded verbatim to ClipGStreamViewer (kind 'clipgstream'). */
  clipgs?: Record<string, unknown>
  /** Extra props forwarded verbatim to FourDViewer (kind 'fourdv'). Call sites
   *  that only pass `dualgs` still get its showUi / showFps / enableXR (so a .4dv
   *  gets XR exactly where a sequence does; `dualgs.enableXR` wins over the
   *  top-level prop, as it does for DualgsViewer); its showTimeline is NOT
   *  forwarded, because callers derive it from sequenceInfo (null for a .4dv)
   *  while a .4dv always has a timeline (FourDViewer defaults it on). */
  fourdv?: Record<string, unknown>
}

/** The ref is forwarded to the active viewer (DualgsViewer exposes
 *  aiEnhance/takeScreenshot/setRenderSize, ClipGStreamViewer exposes
 *  getCanvas/togglePlay/restart, FourDViewer exposes all of those plus
 *  play/pause/seek/getTime; SplatViewer takes no ref). The handle's shape
 *  therefore depends on which viewer is active — hence `any`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ModelViewer = forwardRef<any, ModelViewerProps>(function ModelViewer({
  source,
  className,
  cameraPosition,
  cameraQuaternion,
  onCameraChange,
  onCanvasReady,
  enableXR,
  splat,
  dualgs,
  clipgs,
  fourdv,
}, ref) {
  // 穩健裝置判定(UA + WebXR 探測);clipgstream 路由用它決定 viewer,
  // fourdv 用它決定大檔載入方式(串流)與是否提供 XR。
  const limited = useIsLimitedDevice()

  switch (source.kind) {
    case 'clipgstream':
      // 路由「裝置導向」:非 PC(Quest / iPhone / 行動裝置)一律走 three.js 的
      // ClipGStreamViewerXR —— 它會自動用 24fps 串流 + 有限快取,避免行動裝置
      // 全預載 OOM 閃退,同時能進 WebXR(WebXR 無法算 WebGPU context)。
      // PC 維持桌面 WebGPU 即時版不變。enableXR 只決定要不要顯示 XR 進入按鈕,
      // 不影響路由(桌面 WebGPU 版本就沒有 XR)。
      //
      // limited === null:WebXR 支援度還在探測 → 先掛個同尺寸佔位,別急著掛桌面
      // WebGPU 版(否則 Quest 會誤上全預載、可能 OOM)。探測很快就會 resolve。
      if (limited === null) {
        return <div className={className} />
      }
      if (limited) {
        return (
          <ClipGStreamViewerXR
            ref={ref}
            dataUrl={source.dataUrl}
            className={className}
            enableXR={!!enableXR}
            limited
            {...clipgs}
          />
        )
      }
      return (
        <ClipGStreamViewer
          ref={ref}
          dataUrl={source.dataUrl}
          audioUrl={source.audioUrl}
          className={className}
          cameraPosition={cameraPosition ?? null}
          cameraQuaternion={cameraQuaternion ?? null}
          onCameraChange={onCameraChange}
          onCanvasReady={onCanvasReady}
          {...clipgs}
        />
      )
    case 'sequence':
    case 'ply':
      return (
        <DualgsViewer
          ref={ref}
          modelUrl={source.kind === 'sequence' ? null : source.modelUrl}
          frameUrls={source.sequenceInfo?.frameUrls ?? null}
          fps={source.sequenceInfo?.fps ?? null}
          audioUrl={source.sequenceInfo?.audioUrl ?? null}
          staticUrl={source.staticUrl ?? null}
          staticTransform={source.staticTransform ?? null}
          className={className}
          cameraPosition={cameraPosition ?? null}
          cameraQuaternion={cameraQuaternion ?? null}
          onCameraChange={onCameraChange}
          onCanvasReady={onCanvasReady}
          enableXR={enableXR}
          {...dualgs}
        />
      )
    case 'fourdv':
      // Every device preloads the whole clip before playing. The viewer makes its WebGL
      // context XR-compatible when the file loads, so hold the same placeholder as
      // clipgstream until the WebXR probe has answered.
      // XR as for a sequence (`dualgs.enableXR` wins over the top-level prop), but only
      // where it can be offered at all: a device that is not limited has no WebXR session
      // to offer (the same probe as the button's), and keeps the plain WebGL context.
      if (limited === null) {
        return <div className={className} />
      }
      return (
        <Suspense fallback={<div className={className} />}>
          <FourDViewer
            ref={ref}
            modelUrl={source.modelUrl}
            audioUrl={source.audioUrl}
            timeRange={source.timeRange ?? null}
            className={className}
            cameraPosition={cameraPosition ?? null}
            cameraQuaternion={cameraQuaternion ?? null}
            onCameraChange={onCameraChange}
            onCanvasReady={onCanvasReady}
            enableXR={limited && (typeof dualgs?.enableXR === 'boolean' ? dualgs.enableXR : !!enableXR)}
            showUi={typeof dualgs?.showUi === 'boolean' ? dualgs.showUi : undefined}
            showFps={typeof dualgs?.showFps === 'boolean' ? dualgs.showFps : undefined}
            {...fourdv}
          />
        </Suspense>
      )
    default:
      return (
        <SplatViewer
          modelUrl={source.modelUrl}
          audioUrl={source.audioUrl}
          className={className}
          cameraPosition={cameraPosition ?? undefined}
          cameraQuaternion={cameraQuaternion ?? undefined}
          onCameraChange={onCameraChange}
          onCanvasReady={onCanvasReady}
          {...splat}
        />
      )
  }
})

export default ModelViewer
